/**
 * Bytes the API serves (a PDF, a réclamation attachment) — one definition for every download of the
 * SPA (the outbound documents, the réclamation attachments of both planes):
 *
 *   · `corpsErreurBlob` — a request made with `responseType: "blob"` receives its error body as a Blob
 *     too: turn it back into the JSON body the rest of the client reads (`describeError`, the server's
 *     French reason);
 *   · `ouvrirBlobDansOnglet` — open bytes in a new tab: the tab is opened synchronously inside the
 *     click (popup blockers allow it), then pointed at a Blob URL revoked a minute later; a failed read
 *     closes the tab and rejects. No token ever travels in a URL.
 */

/**
 * @param {unknown} err the rejected value of the API client
 * @returns {Promise<unknown>} the parsed body, `{ success: false, message: null }` when unreadable, or `err` itself
 */
export async function corpsErreurBlob(err) {
  if (typeof Blob !== "undefined" && err instanceof Blob) {
    try {
      return JSON.parse(await err.text());
    } catch {
      return { success: false, message: null };
    }
  }
  return err;
}

/** How long a Blob URL handed to a tab stays valid. */
export const DUREE_URL_BLOB_MS = 60_000;

/**
 * @param {() => Promise<Blob|ArrayBuffer>} lire reads the bytes (called after the tab is opened)
 * @param {{ mime?: string }} [o] the type to give bytes that come back untyped
 * @returns {Promise<Window|null>} the tab (null when the browser refused to open one)
 */
export async function ouvrirBlobDansOnglet(lire, { mime = "application/octet-stream" } = {}) {
  const win = typeof window !== "undefined" ? window.open("", "_blank") : null;
  try {
    const blob = await lire();
    const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob], { type: mime }));
    if (win) {
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), DUREE_URL_BLOB_MS);
    } else {
      URL.revokeObjectURL(url);
    }
    return win;
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

/**
 * Save bytes under a name (an attachment, a document): a Blob URL clicked through a temporary
 * `<a download>`, revoked once the browser has taken it. No token ever travels in a URL.
 * @param {Blob|ArrayBuffer} blob
 * @param {string} nom the file name offered to the user
 * @param {{ mime?: string }} [o] the type to give bytes that come back untyped
 */
export function telechargerBlob(blob, nom, { mime = "application/octet-stream" } = {}) {
  if (typeof document === "undefined") return;
  const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nom || "document";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), DUREE_URL_BLOB_MS);
}
